import { type KeyboardEvent, useCallback, useEffect, useMemo } from 'react';

type Options = {
  count: number;
  index: number;
  onIndexChange: (index: number) => void;
  /** Suspends paging — used while an image is zoomed in and the arrows pan it. */
  disabled?: boolean;
  /**
   * Whether the viewer is actually showing.
   *
   * A closed MUI `Dialog` still renders its component, so without this the
   * clamp below runs against a stack that is not on screen — and when the
   * gallery is filtered to videos that stack is empty, so it would pin the
   * index at 0 behind the visitor's back.
   */
  enabled?: boolean;
};

export type LightboxNavigation = {
  /** Clamped, and wrapped: the last image's "next" is the first. */
  index: number;
  next: () => void;
  previous: () => void;
  first: () => void;
  last: () => void;
  /** Attach to the dialog's paper. Handles the arrow, Home and End keys. */
  onKeyDown: (event: KeyboardEvent) => void;
  /** '3 / 12', for the counter. */
  position: string;
};

/**
 * Paging a stack of images: the clamping, the wrap, and the keys.
 *
 * Wrapping rather than stopping at the ends, because a lightbox is a loop in
 * every gallery anyone has used and a dead arrow at image twelve reads as a
 * bug. Escape, the focus trap, the inert background and returning focus to the
 * tile that was clicked are all MUI's `Dialog`, which is the reason the
 * viewers are dialogs and not fixed-position divs — that is a lot of WCAG to
 * re-implement by hand, and 2.1.2 is unforgiving about getting it wrong.
 *
 * The index is clamped on the way out rather than corrected in state: the
 * owner's index outlives the viewer being closed (see `useMediaViewer`), and
 * the stack it points into can be shorter next time the viewer opens — a
 * filter chip is all it takes.
 */
export function useLightboxNavigation({
  count,
  index,
  onIndexChange,
  disabled = false,
  enabled = true,
}: Options): LightboxNavigation {
  const safeIndex = count > 0 ? Math.min(Math.max(index, 0), count - 1) : 0;

  // A filter can shorten the stack under an open viewer, so the clamp is
  // pushed back into the owner's state as well as applied here. Without it the
  // counter would read "12 / 4" until the next arrow press. Only while the
  // viewer is open: see `enabled`.
  useEffect(() => {
    if (enabled && safeIndex !== index) onIndexChange(safeIndex);
  }, [enabled, safeIndex, index, onIndexChange]);

  const go = useCallback(
    (next: number) => {
      if (count < 1) return;
      onIndexChange(((next % count) + count) % count);
    },
    [count, onIndexChange],
  );

  const next = useCallback(() => go(safeIndex + 1), [go, safeIndex]);
  const previous = useCallback(() => go(safeIndex - 1), [go, safeIndex]);
  const first = useCallback(() => go(0), [go]);
  const last = useCallback(() => go(count - 1), [go, count]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (disabled || count < 2) return;
      // Only the keys below are claimed. Tab has to keep reaching the dialog's
      // own controls, and Escape belongs to the Dialog.
      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          next();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          previous();
          break;
        case 'Home':
          event.preventDefault();
          first();
          break;
        case 'End':
          event.preventDefault();
          last();
          break;
        default:
          break;
      }
    },
    [disabled, count, next, previous, first, last],
  );

  const position = useMemo(() => `${count === 0 ? 0 : safeIndex + 1} / ${count}`, [safeIndex, count]);

  return { index: safeIndex, next, previous, first, last, onKeyDown, position };
}
